import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout, Card, Descriptions, Spin, Result, Typography, theme, Space, Button, App, Row, Col, Statistic, Divider } from 'antd';
import {
  CalculatorOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  DollarOutlined,
  PercentageOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  PlayCircleOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchSharedResult, claimShare } from '../api/client';
import { useAuthStore } from '../stores/authStore';

const SharedView: React.FC = () => {
  const { t } = useTranslation();
  const { shareId } = useParams<{ shareId: string }>();
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    if (!shareId) return;
    setLoading(true);
    fetchSharedResult(shareId)
      .then((res) => {
        setData(res);
      })
      .catch((e) => {
        console.error('Share load error:', e);
        const status = e?.response?.status;
        if (status === 404) {
          setError(t('idea_calc.shared_view.error_not_found'));
        } else if (status === 503) {
          setError(t('idea_calc.shared_view.error_db_unavailable'));
        } else {
          setError(t('idea_calc.shared_view.error_generic'));
        }
      })
      .finally(() => setLoading(false));
  }, [shareId]);

  const handleClaim = async () => {
    if (!shareId) return;
    setClaiming(true);
    try {
      await claimShare(shareId);
      setClaimed(true);
      message.success(t('idea_calc.shared_view.msg_added_success'));
    } catch {
      message.error(t('idea_calc.shared_view.msg_added_error'));
    } finally {
      setClaiming(false);
    }
  };

  const handleLoadIntoCalculator = () => {
    if (!shareId) return;
    sessionStorage.setItem('loadShareId', shareId);
    navigate('/');
  };

  const calcData = (data?.calculation_data as Record<string, unknown>) || data || {};
  const profitData = (data?.profitability as Record<string, unknown>) || {};
  const budgetData = (data?.budget_data as { categories?: { id: string; name: string; color: string; items: { name: string; amount: number }[] }[] }) || {};

  const fmt = (v: unknown) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return String(v);
  };

  const fmtMoney = (v: unknown) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return String(v);
  };

  // Calculate budget total
  const budgetTotal = useMemo(() => {
    if (!budgetData.categories) return 0;
    return budgetData.categories.reduce((sum, cat) => {
      return sum + (cat.items?.reduce((s, item) => s + (item.amount || 0), 0) || 0);
    }, 0);
  }, [budgetData]);

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Layout.Header
        style={{
          background: '#001029',
          display: 'flex',
          alignItems: 'center',
          height: 64,
          padding: '0 24px',
          gap: 12,
        }}
      >
        <CalculatorOutlined style={{ fontSize: 24, color: '#69b1ff' }} />
        <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
          {t('idea_calc.shared_view.header_title')}
        </Typography.Title>
        <div style={{ flex: 1 }} />
        <Link to="/" style={{ color: '#69b1ff', display: 'flex', alignItems: 'center', gap: 4 }}>
          <HomeOutlined /> {t('idea_calc.shared_view.back_to_calculator')}
        </Link>
      </Layout.Header>
      <Layout.Content style={{ maxWidth: 900, width: '100%', margin: '24px auto', padding: '0 16px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
          </div>
        )}
        {error && <Result status="warning" title={error} />}
        {!loading && !error && data && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {data.fileName ? (
              <Typography.Title level={3}>{String(data.fileName)}</Typography.Title>
            ) : null}

            <Space size={8}>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleLoadIntoCalculator}
              >
                {t('idea_calc.shared_view.load_into_calculator')}
              </Button>
              {isAuthenticated && (
                <>
                  {!claimed ? (
                    <Button
                      icon={<PlusCircleOutlined />}
                      loading={claiming}
                      onClick={handleClaim}
                    >
                      {t('idea_calc.shared_view.add_to_history')}
                    </Button>
                  ) : (
                    <Button disabled>{t('idea_calc.shared_view.added')}</Button>
                  )}
                </>
              )}
            </Space>

            {/* Profitability Summary */}
            {Object.keys(profitData).length > 0 && (
              <Card title={t('idea_calc.shared_view.section_profitability')} size="small">
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><DollarOutlined /> {t('idea_calc.shared_view.stat_profit_per_unit')}</>}
                      value={profitData.profit_per_unit_with_costs as number}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: (profitData.profit_per_unit_with_costs as number) >= 0 ? '#3f8600' : '#cf1322' }}
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><PercentageOutlined /> {t('idea_calc.shared_view.stat_margin')}</>}
                      value={profitData.batch_margin_percentage as number}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: (profitData.batch_margin_percentage as number) >= 20 ? '#3f8600' : '#faad14' }}
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><RiseOutlined /> {t('idea_calc.shared_view.stat_roi')}</>}
                      value={profitData.batch_roi as number}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: (profitData.batch_roi as number) >= 50 ? '#3f8600' : '#faad14' }}
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><ShoppingCartOutlined /> {t('idea_calc.shared_view.stat_batch_profit')}</>}
                      value={profitData.total_batch_final_profit_with_costs as number}
                      precision={0}
                      prefix="$"
                      valueStyle={{ color: (profitData.total_batch_final_profit_with_costs as number) >= 0 ? '#3f8600' : '#cf1322' }}
                    />
                  </Col>
                  {/* Снимки, сохранённые до появления МЗЦ, этого числа не содержат —
                      показываем только то, что реально посчитано, без подстановки нуля. */}
                  {typeof profitData.min_purchase_price === 'number' && (
                    <Col xs={12} sm={8} md={6}>
                      <Statistic
                        title={<><TagOutlined /> {t('idea_calc.shared_view.stat_min_purchase_price')}</>}
                        value={profitData.min_purchase_price}
                        precision={2}
                        prefix="$"
                        valueStyle={{ color: profitData.min_purchase_price >= 0 ? '#3f8600' : '#cf1322' }}
                      />
                    </Col>
                  )}
                </Row>
                <Divider style={{ margin: '16px 0' }} />
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="COGS">{fmtMoney(profitData.total_cost)}</Descriptions.Item>
                  <Descriptions.Item label="Gross Profit">{fmtMoney(profitData.gross_profit)}</Descriptions.Item>
                  <Descriptions.Item label="Final Profit">{fmtMoney(profitData.final_profit)}</Descriptions.Item>
                  <Descriptions.Item label="Referral Fee">{fmtMoney(profitData.referral_fee)}</Descriptions.Item>
                  <Descriptions.Item label="FBA Fee">{fmtMoney(profitData.fba_fee)}</Descriptions.Item>
                  <Descriptions.Item label="Return Loss">{fmtMoney(profitData.return_loss)}</Descriptions.Item>
                  <Descriptions.Item label={t('idea_calc.shared_view_table.ppc_per_unit_label')}>{fmtMoney(profitData.ppc_cost_per_unit)}</Descriptions.Item>
                  <Descriptions.Item label="PPC Total">{fmtMoney(profitData.ppc_cost_total)}</Descriptions.Item>
                  <Descriptions.Item label="Storage Fee">{fmtMoney(profitData.total_storage_fee)}</Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Budget */}
            {budgetData.categories && budgetData.categories.length > 0 && (
              <Card
                title={t('idea_calc.shared_view.section_launch_budget')}
                size="small"
                extra={<Typography.Text strong>${budgetTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</Typography.Text>}
              >
                <Row gutter={[12, 12]}>
                  {budgetData.categories.map((cat) => {
                    const catTotal = cat.items?.reduce((s, item) => s + (item.amount || 0), 0) || 0;
                    return (
                      <Col key={cat.id} xs={24} sm={12}>
                        <div style={{ background: '#fafafa', borderRadius: 6, padding: '8px 12px', border: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: cat.color }} />
                            <Typography.Text strong style={{ fontSize: 12 }}>{cat.name}</Typography.Text>
                            <Typography.Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                              ${catTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                            </Typography.Text>
                          </div>
                          {cat.items && cat.items.length > 0 && (
                            <div style={{ fontSize: 11, color: '#666' }}>
                              {cat.items.filter(i => i.amount > 0).map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{item.name || t('idea_calc.shared_view.no_name')}</span>
                                  <span>${item.amount.toFixed(0)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}

            <Card title={t('idea_calc.shared_view.section_calc_params')} size="small">
              <Descriptions column={2} size="small" bordered>
                {calcData.parent_asin ? (
                  <Descriptions.Item label="ASIN">{fmt(calcData.parent_asin)}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label={t('idea_calc.shared_view.row_purchase_price')}>${fmt(calcData.purchase_price)}</Descriptions.Item>
                <Descriptions.Item label={t('idea_calc.shared_view.row_shipping')}>${fmt(calcData.shipping_price)}</Descriptions.Item>
                <Descriptions.Item label={t('idea_calc.shared_view.row_selling_price')}>${fmt(calcData.selling_price)}</Descriptions.Item>
                <Descriptions.Item label="Referral Fee">{fmt(calcData.referral_fee)}%</Descriptions.Item>
                <Descriptions.Item label="FBA Fee">${fmt(calcData.fba_fee)}</Descriptions.Item>
                <Descriptions.Item label="PPC Cost">${fmt(calcData.ppc_cost)}</Descriptions.Item>
                <Descriptions.Item label="Conversion Rate">{fmt(calcData.conversion_rate)}%</Descriptions.Item>
                <Descriptions.Item label="PPC Sales %">{fmt(calcData.ppc_sales_percentage)}%</Descriptions.Item>
                <Descriptions.Item label="Return Rate">{fmt(calcData.return_rate)}%</Descriptions.Item>
                <Descriptions.Item label="Monthly Sales">{fmt(calcData.monthly_sales)}</Descriptions.Item>
                <Descriptions.Item label="Batch Qty">{fmt(calcData.batch_quantity)}</Descriptions.Item>
              </Descriptions>
            </Card>
            {Array.isArray(calcData.competitors) && (calcData.competitors as unknown[]).length > 0 ? (
              <Card title={t('idea_calc.shared_view.section_competitors')} size="small">
                <Typography.Text type="secondary">
                  {(calcData.competitors as { asin: string }[]).map((c) => c.asin).join(', ')}
                </Typography.Text>
              </Card>
            ) : null}
          </Space>
        )}
      </Layout.Content>
    </Layout>
  );
};

export default SharedView;
